import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Mail,
  Phone,
  ShieldCheck,
  User as UserIcon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';

/**
 * Sign-in interception page reached when a guest taps "Place Order" on
 * /checkout. Internal step state: 'prompt' → 'phone' → 'otp' → 'profile'.
 *
 * The Checkout component saves a draft of its form/selections to
 * sessionStorage before navigating here, so on return to /checkout the
 * user's choices are restored. We signal the return via location.state:
 *  - { resumedFromSignIn: true }  — auth completed, hydrate and stay
 *  - { autoPlaceAsGuest: true }   — user picked "Continue as Guest"
 */
const CheckoutSignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, phoneLogin, phoneRegister, sendOtp } = useAuth();

  const [step, setStep] = useState('prompt');
  const [phone, setPhone] = useState(
    (location.state?.prefillPhone || '').replace(/\D/g, '').slice(0, 10),
  );
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const [pendingState, setPendingState] = useState(null); // { exists: boolean, otp: string }

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const getE164Phone = () => `+91${phone.replace(/\D/g, '')}`;

  // ── Guard rails ───────────────────────────────────────────────────────────
  // Defense in depth: if the user lands here while already authenticated,
  // bounce them back to checkout — they have nothing to do here.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/checkout', {
        replace: true,
        state: { resumedFromSignIn: true },
      });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  // ── Step handlers ─────────────────────────────────────────────────────────
  const onContinueAsGuest = () => {
    navigate('/checkout', { state: { autoPlaceAsGuest: true } });
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsLoading(true);
    try {
      const existsResult = await authService.checkPhoneExists(getE164Phone());
      if (!existsResult.success) {
        setError(existsResult.error || 'Could not reach the server. Please try again.');
        setIsLoading(false);
        return;
      }
      const sendResult = await sendOtp(getE164Phone());
      if (!sendResult.success) {
        setError(sendResult.error || 'Failed to send OTP. Please try again.');
        setIsLoading(false);
        return;
      }
      setStep('otp');
      setCountdown(sendResult.cooldownSeconds || 60);
      setPendingState({ exists: existsResult.exists, otp: null });
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    setIsLoading(true);
    try {
      if (pendingState?.exists) {
        const result = await phoneLogin(getE164Phone(), otp);
        if (result.success) {
          navigate('/checkout', {
            replace: true,
            state: { resumedFromSignIn: true },
          });
        } else {
          setError(result.error || 'Sign-in failed. Please try again.');
        }
      } else {
        // New user — collect name/email on the next step
        setPendingState({ exists: false, otp });
        setStep('profile');
      }
    } catch (err) {
      setError('OTP verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    setError('');
    setIsLoading(true);
    try {
      const result = await sendOtp(getE164Phone());
      if (result.success) {
        setCountdown(result.cooldownSeconds || 60);
        setOtp('');
      } else {
        setError(result.error || 'Failed to resend OTP.');
      }
    } catch {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (trimmedName.length < 2) {
      setError('Please enter your full name');
      return;
    }
    if (
      trimmedEmail &&
      !/^[\w.+\-]+@[\w-]+\.[a-zA-Z]{2,}$/.test(trimmedEmail)
    ) {
      setError('Please enter a valid email address');
      return;
    }

    const verifiedOtp = pendingState?.otp;
    if (!verifiedOtp) {
      setError('Session expired. Please start the sign-in again.');
      setStep('prompt');
      return;
    }

    setIsLoading(true);
    try {
      const result = await phoneRegister(
        trimmedName,
        getE164Phone(),
        verifiedOtp,
        trimmedEmail || undefined,
        new Date().toISOString(),
      );
      if (result.success) {
        navigate('/checkout', {
          replace: true,
          state: { resumedFromSignIn: true },
        });
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Layout ────────────────────────────────────────────────────────────────
  const shell = (children) => (
    <div className="min-h-screen bg-gradient-to-br from-[#1a2f0f] via-[#2e4e18] to-[#3f6b20] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {children}
        </div>
      </div>
    </div>
  );

  const errorBanner = error ? (
    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
      {error}
    </div>
  ) : null;

  if (step === 'prompt') {
    return shell(
      <>
        <button
          type="button"
          onClick={() => navigate('/checkout')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to checkout
        </button>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">One last step</h2>
        <p className="text-gray-600 mb-6">
          Sign in to track orders, save addresses, and use your BMF Coins — or
          place this order as a guest.
        </p>
        {errorBanner}
        <button
          type="button"
          onClick={() => setStep('phone')}
          className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300"
        >
          Sign In <ArrowRight className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={onContinueAsGuest}
          className="w-full mt-3 bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 py-3 rounded-xl font-semibold transition-all duration-300"
        >
          Continue as Guest
        </button>
        <p className="mt-6 text-xs text-gray-500 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          Your checkout details are saved — nothing is lost.
        </p>
      </>,
    );
  }

  if (step === 'phone') {
    return shell(
      <>
        <button
          type="button"
          onClick={() => setStep('prompt')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Sign In</h2>
        <p className="text-gray-600 mb-6">
          Enter your mobile number — we'll send a one-time code.
        </p>
        {errorBanner}
        <form onSubmit={handlePhoneSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mobile Number
            </label>
            <div className="relative flex">
              <span className="inline-flex items-center px-4 py-3 border border-r-0 border-gray-300 rounded-l-xl bg-gray-50 text-gray-600 text-sm font-medium">
                +91
              </span>
              <div className="relative flex-1">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit mobile number"
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-r-xl focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all"
                  maxLength={10}
                  required
                />
              </div>
            </div>
          </div>
          <button
            id="send-otp-button"
            type="submit"
            disabled={isLoading || phone.replace(/\D/g, '').length !== 10}
            className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>Continue <ArrowRight className="w-5 h-5" /></>
            )}
          </button>
        </form>
      </>,
    );
  }

  if (step === 'otp') {
    return shell(
      <>
        <button
          type="button"
          onClick={() => setStep('phone')}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Change number
        </button>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Enter OTP</h2>
        <p className="text-gray-600 mb-6">
          Sent to <span className="font-semibold">+91 {phone}</span>
        </p>
        {errorBanner}
        <form onSubmit={handleOtpSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              6-digit code
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center tracking-[0.5em] text-lg font-semibold focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all"
              maxLength={6}
              required
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || otp.length !== 6}
            className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>Verify <CheckCircle2 className="w-5 h-5" /></>
            )}
          </button>
          <button
            type="button"
            onClick={handleResendOtp}
            disabled={countdown > 0 || isLoading}
            className="w-full text-sm text-gray-600 hover:text-[#548B2E] disabled:text-gray-400"
          >
            {countdown > 0 ? `Resend in 0:${String(countdown).padStart(2, '0')}` : 'Resend code'}
          </button>
        </form>
      </>,
    );
  }

  // step === 'profile' — back button intentionally omitted; OTP already burned.
  return shell(
    <>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Tell us your name</h2>
      <p className="text-gray-600 mb-6">
        We'll use this on your orders and invoices.
      </p>
      {errorBanner}
      <form onSubmit={handleProfileSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Riya Sharma"
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#548B2E] focus:border-transparent transition-all"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Used only for order receipts. We never share it.
          </p>
        </div>
        <button
          type="submit"
          disabled={isLoading || name.trim().length < 2}
          className="w-full bg-[#548B2E] hover:bg-[#3f6b20] text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>Complete sign-up <CheckCircle2 className="w-5 h-5" /></>
          )}
        </button>
      </form>
    </>,
  );
};

export default CheckoutSignIn;
